import React, { useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Play, ChevronLeft, ChevronRight, ExternalLink, Star } from "lucide-react";

const IMDB_URL = "https://www.imdb.com/name/nm10602133/?ref_=fn_t_1";
const IMDB_CREDIT_COUNT = 42;

const FILMS = [
  { id: "wJQmsLOrkx4", title: "Parasomnia", genre: "Horror", thumb: "/thumbs/parasomnia.jpg" },
  { id: "dcm7ma4_3Bk", title: "Sorry for Your Loss", genre: "Drama", thumb: "/thumbs/sorry-for-your-loss.jpg" },
  { id: "Z_dacyn_M-A", title: "Finding Daddy", genre: "Comedy", thumb: "/thumbs/finding-daddy.jpg" },
  { id: "_9GKhMfkD_I", title: "Knead", genre: "Sci-Fi", thumb: "/thumbs/knead.jpg" },
  { id: "az5X2GRxX0I", title: "Golden Garden Takeout", genre: "Drama", thumb: "/thumbs/golden-garden-takeout.jpg" },
  { id: "Ho2rxu_CsM4", title: "Cicada Clouds", genre: "Animation", thumb: "/thumbs/cicada-clouds.jpg" },
  { id: "5ZHpFcIJEmY", title: "Tapestry", genre: "Horror", thumb: "/thumbs/tapestry.jpg" },
  { id: "Hh-gYXEY9Xo", title: "BestFriendAI", genre: "Drama / Sci-Fi", thumb: "/thumbs/bestfriendai.jpg" },
  { id: "2n_o3scFBwg", title: "Lady Like", genre: "Documentary", thumb: "/thumbs/lady-like.jpg" },
];

const ytThumb = (id) => `https://img.youtube.com/vi/${id}/maxresdefault.jpg`;
const ytEmbed = (f) =>
  `https://www.youtube.com/embed/${f.id}?autoplay=1&rel=0${f.start ? `&start=${f.start}` : ""}`;

// Light bulb strip — used along top/bottom edges of the marquee
const BulbStrip = ({ count = 28, vertical = false, className = "" }) => (
  <div
    aria-hidden="true"
    className={`flex ${vertical ? "flex-col" : "flex-row"} ${vertical ? "justify-around" : "justify-around"} items-center ${className}`}
  >
    {Array.from({ length: count }).map((_, i) => (
      <span
        key={i}
        className="block w-2.5 h-2.5 rounded-full"
        style={{
          background:
            "radial-gradient(circle at 30% 30%, #fff5b8 0%, #f5d460 35%, #b88a1a 80%, #5a3a05 100%)",
          boxShadow:
            "0 0 6px rgba(255,224,128,0.7), 0 0 14px rgba(255,196,90,0.45)",
        }}
      />
    ))}
  </div>
);

export const PastWork = () => {
  const railRef = useRef(null);
  const [active, setActive] = useState(null);

  const scroll = (dir) => {
    const el = railRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.85, behavior: "smooth" });
  };

  return (
    <section
      id="work"
      data-testid="past-work-section"
      className="relative bg-[#0d0d0d] py-16 md:py-20 overflow-hidden"
    >
      <div className="mx-auto max-w-[1500px] px-4 sm:px-6 md:px-10 relative">
        {/* Marquee header with illuminated sign */}
        <div className="flex items-end justify-between mb-6 gap-4">
          <div
            data-testid="marquee-sign"
            className="relative inline-block px-6 py-3"
            style={{
              background:
                "linear-gradient(180deg, #2d160b 0%, #1a0d06 100%)",
              border: "2px solid #6b4a2a",
              borderRadius: "6px",
              boxShadow:
                "inset 0 0 18px rgba(255,180,60,0.18), 0 6px 22px rgba(0,0,0,0.5)",
            }}
          >
            <BulbStrip count={14} className="absolute -top-2 left-3 right-3" />
            <h2
              data-testid="work-headline"
              className="font-display font-black text-xl md:text-[26px] tracking-[0.18em] text-[#f5d460]"
              style={{
                textShadow:
                  "0 0 12px rgba(245,212,96,0.55), 0 0 4px rgba(255,255,255,0.3)",
              }}
            >
              RECENT PROJECTS
            </h2>
            <BulbStrip count={14} className="absolute -bottom-2 left-3 right-3" />
          </div>
        </div>

        {/* Wood-framed poster wall with bulb strings on sides */}
        <div
          className="relative p-4 md:p-5 pr-9 md:pr-10"
          style={{
            background:
              "linear-gradient(180deg, #2a1a0e 0%, #150c06 50%, #2a1a0e 100%)",
            border: "3px solid #6b4a2a",
            borderRadius: "10px",
            boxShadow:
              "inset 0 0 60px rgba(0,0,0,0.6), 0 18px 50px rgba(0,0,0,0.55)",
          }}
        >
          {/* Bulb strings along left and right — centered in the gutter between frame edge and posters */}
          <BulbStrip
            count={8}
            vertical
            className="absolute left-3 md:left-4 top-3 bottom-3"
          />
          <BulbStrip
            count={8}
            vertical
            className="absolute right-3 md:right-4 top-3 bottom-3"
          />

          {/* Poster rail */}
          <div
            ref={railRef}
            data-testid="work-rail"
            className="no-scrollbar overflow-x-auto scroll-smooth"
          >
            <div className="flex gap-3 md:gap-4 min-w-max px-5">
              {/* IMDb full-filmography tile — first in the rail */}
              <a
                href={IMDB_URL}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="imdb-tile"
                className="group relative shrink-0 w-[170px] sm:w-[200px] md:w-[230px] text-left"
              >
                <div
                  className="relative p-1.5 transition-transform duration-300 group-hover:-translate-y-1"
                  style={{
                    background:
                      "linear-gradient(180deg, #4a2f18 0%, #1c1108 100%)",
                    border: "1px solid #6b4a2a",
                    boxShadow:
                      "0 10px 22px rgba(0,0,0,0.55), inset 0 0 8px rgba(0,0,0,0.6)",
                  }}
                >
                  <div className="relative aspect-[2/3] overflow-hidden bg-[#0d0d0d] flex flex-col">
                    <div
                      className="flex items-center justify-center py-2"
                      style={{ background: "#f5c518" }}
                    >
                      <span
                        className="font-display font-black text-[20px] tracking-[-0.03em] text-[#000] leading-none"
                        style={{ letterSpacing: "-0.5px" }}
                      >
                        IMDb
                      </span>
                    </div>

                    <div className="flex-1 flex flex-col items-center justify-center px-3 pt-3 pb-2 text-center">
                      <div
                        className="font-display font-black text-[54px] sm:text-[58px] md:text-[62px] leading-none text-[#f5c518]"
                        style={{
                          textShadow: "0 2px 0 rgba(0,0,0,0.4), 0 0 22px rgba(245,197,24,0.35)",
                        }}
                      >
                        {IMDB_CREDIT_COUNT}
                        <span className="text-[#f5c518]">+</span>
                      </div>
                      <div className="font-mono-indie text-[10px] tracking-[0.22em] text-[#cbb88a] mt-1">
                        MOVIE CREDITS
                      </div>

                      <ul className="mt-4 w-full space-y-1 text-left">
                        {FILMS.slice(0, 4).map((f) => (
                          <li
                            key={f.id}
                            className="flex items-center gap-1.5 font-mono-indie text-[9.5px] text-[#cbb88a]/85 leading-tight"
                          >
                            <Star size={8} fill="#f5c518" strokeWidth={0} className="shrink-0" />
                            <span className="truncate">{f.title}</span>
                          </li>
                        ))}
                        <li className="font-mono-indie text-[9px] text-[#8a7d5a] leading-tight pl-3.5 pt-0.5">
                          + {IMDB_CREDIT_COUNT - 4} more titles…
                        </li>
                      </ul>
                    </div>

                    <div
                      className="flex items-center justify-center gap-1.5 py-2 border-t border-[#3a2a12] group-hover:bg-[#f5c518] transition-colors"
                      style={{ background: "#1a1108" }}
                    >
                      <span className="font-mono-indie font-semibold text-[10px] tracking-[0.18em] text-[#f5c518] group-hover:text-[#000]">
                        VIEW ON IMDb
                      </span>
                      <ExternalLink
                        size={11}
                        className="text-[#f5c518] group-hover:text-[#000]"
                      />
                    </div>
                  </div>
                </div>
                <div className="mt-2 px-1">
                  <div className="font-display font-black text-[13px] md:text-[14px] text-[#f5d460] tracking-[0.05em] leading-tight">
                    Full Filmography
                  </div>
                  <div className="font-mono-indie text-[10px] text-[#cbb88a]/80 mt-1">
                    IMDb · {IMDB_CREDIT_COUNT}+ credits
                  </div>
                </div>
              </a>

              {FILMS.map((f) => (
                <button
                  key={f.id}
                  data-testid={`work-tile-${f.id}`}
                  onClick={() => setActive(f)}
                  className="group relative shrink-0 w-[170px] sm:w-[200px] md:w-[230px] text-left"
                >
                  {/* Frame */}
                  <div
                    className="relative p-1.5 transition-transform duration-300 group-hover:-translate-y-1"
                    style={{
                      background:
                        "linear-gradient(180deg, #4a2f18 0%, #1c1108 100%)",
                      border: "1px solid #6b4a2a",
                      boxShadow:
                        "0 10px 22px rgba(0,0,0,0.55), inset 0 0 8px rgba(0,0,0,0.6)",
                    }}
                  >
                    <div className="relative aspect-[2/3] overflow-hidden bg-black">
                      <img
                        src={f.thumb || ytThumb(f.id)}
                        alt={f.title}
                        onError={(e) => {
                          e.currentTarget.src = `https://img.youtube.com/vi/${f.id}/hqdefault.jpg`;
                        }}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black/15 group-hover:bg-black/40 transition-colors" />
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <span className="w-12 h-12 rounded-full bg-[#e8472b] flex items-center justify-center shadow-lg">
                          <Play size={18} fill="#f4f1ea" color="#f4f1ea" />
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Title plaque under poster */}
                  <div className="mt-2 px-1">
                    <div className="font-display font-black text-[13px] md:text-[14px] text-[#f5d460] tracking-[0.05em] leading-tight">
                      {f.title}
                    </div>
                    <div className="font-mono-indie text-[10px] text-[#cbb88a]/80 mt-1">
                      {f.genre}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Integrated rail arrows — mounted into the wooden frame */}
          <button
            data-testid="work-prev"
            onClick={() => scroll(-1)}
            aria-label="Previous"
            className="hidden md:flex absolute left-[-18px] top-1/2 -translate-y-1/2 items-center justify-center w-11 h-16 z-10 transition-transform hover:-translate-x-0.5"
            style={{
              background:
                "linear-gradient(180deg, #4a2f18 0%, #2a1a0e 55%, #1c1108 100%)",
              border: "2px solid #6b4a2a",
              borderRadius: "6px 3px 3px 6px",
              boxShadow:
                "0 8px 18px rgba(0,0,0,0.6), inset 0 0 10px rgba(255,180,60,0.15), inset 2px 0 0 rgba(255,220,140,0.15)",
              color: "#f5d460",
            }}
          >
            <ChevronLeft size={20} strokeWidth={2.4} />
          </button>

          <button
            data-testid="work-next"
            onClick={() => scroll(1)}
            aria-label="Next"
            className="hidden md:flex absolute right-[-18px] top-1/2 -translate-y-1/2 items-center justify-center w-11 h-16 z-10 transition-transform hover:translate-x-0.5"
            style={{
              background:
                "linear-gradient(180deg, #4a2f18 0%, #2a1a0e 55%, #1c1108 100%)",
              border: "2px solid #6b4a2a",
              borderRadius: "3px 6px 6px 3px",
              boxShadow:
                "0 8px 18px rgba(0,0,0,0.6), inset 0 0 10px rgba(255,180,60,0.15), inset -2px 0 0 rgba(255,220,140,0.15)",
              color: "#f5d460",
            }}
          >
            <ChevronRight size={20} strokeWidth={2.4} />
          </button>

          {/* Mobile arrows below rail */}
          <div className="flex md:hidden items-center justify-center gap-3 mt-3">
            <button
              data-testid="work-prev-mobile"
              onClick={() => scroll(-1)}
              aria-label="Previous"
              className="w-10 h-10 rounded-full border-2 border-[#6b4a2a] text-[#f5d460] flex items-center justify-center"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              data-testid="work-next-mobile"
              onClick={() => scroll(1)}
              aria-label="Next"
              className="w-10 h-10 rounded-full border-2 border-[#6b4a2a] text-[#f5d460] flex items-center justify-center"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          data-testid="work-dialog"
          className="max-w-3xl bg-[#0d0d0d] border-2 border-[#6b4a2a] p-0 overflow-hidden"
        >
          {active && (
            <>
              <DialogHeader className="p-5 pb-3 border-b border-[#6b4a2a]">
                <DialogTitle className="font-display font-black text-xl tracking-tight text-[#f5d460]">
                  {active.title}
                </DialogTitle>
                <DialogDescription className="font-mono-indie text-[11px] uppercase tracking-[0.18em] text-[#cbb88a]">
                  {active.genre}
                </DialogDescription>
              </DialogHeader>
              <div className="bg-black aspect-video">
                <iframe
                  data-testid="work-video"
                  key={active.id}
                  src={ytEmbed(active)}
                  title={active.title}
                  allow="autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                  frameBorder="0"
                />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default PastWork;
